import * as _ from 'lodash';
import { Helmet } from 'react-helmet';
import type { FunctionComponent, VoidFunctionComponent } from 'react';
import type { SetRequired } from 'type-fest';

import Locator from '../../../../shared/Locator';
import usePackageVersionProjects from '../../../hooks/api/packages/usePackageVersionProjects';
import usePackageVulnerabilities from '../../../hooks/api/packages/usePackageVulnerabilities';
import usePackageVersionSearchParams from '../../../hooks/usePackageVersionSearchParams';
import type { PackageVersionSearchParams } from '../../../hooks/usePackageVersionSearchParams';
import { nuc } from '../../../utils';
import SecurityRequired from '../../generic/Gates/SecurityRequired';
import Subnav from '../../generic/Subnav';
import SidebarLayout from '../../layouts/SidebarLayout';
import PackageInventoryFilters from '../filters';
import { PackageVersionFiltersPackagesContainer } from '../filters/PackageFiltersForVersion';
import { PackageInventoryFiltersProject as PackageInventoryProjectFilters } from '../filters/ProjectFilters';
import type { RouterProps } from '../../../types';
import type { PackageInventoryParams } from '../PackageInventoryContainer';

import PackageProjectsTable from './PackageProjectsTable';
import PackageVersionCTA from './PackageVersionCTA';

type NavKey = 'projects' | 'vulnerabilities';

function isNavKey(value: string | undefined): value is NavKey {
  return value === 'projects' || value === 'vulnerabilities';
}

const SubNav: VoidFunctionComponent<{
  navKey: NavKey;
  packageLocator: string;
  version: string;
  searchParams: PackageVersionSearchParams;
}> = ({ navKey, packageLocator, version, searchParams }) => {
  const { data: projects, isLoading: areProjectsLoading } = usePackageVersionProjects(
    packageLocator,
    version,
    searchParams.params,
  );
  const { data: vulnerabilities = [], isLoading: areVulnerabilitiesLoading } = usePackageVulnerabilities(
    packageLocator,
    version,
  );
  const { fetcher } = Locator.parse(packageLocator);
  const shouldDisplayPackageVulns = fetcher !== 'csbinary';

  return (
    <Subnav activeKey={navKey}>
      <Subnav.ItemWithCount
        to={`/packages/${nuc(packageLocator)}/${encodeURIComponent(version)}/projects?${searchParams.queryString}`}
        navKey="projects"
        isLoading={areProjectsLoading}
        count={projects?.count ?? 0}
      >
        Projects
      </Subnav.ItemWithCount>
      { shouldDisplayPackageVulns &&
      <Subnav.ItemWithCount
        to={`/packages/${nuc(packageLocator)}/${encodeURIComponent(version)}/vulnerabilities?${searchParams.queryString}`}
        navKey="vulnerabilities"
        isLoading={areVulnerabilitiesLoading}
        count={vulnerabilities.length}
      >
        Vulnerabilities
      </Subnav.ItemWithCount> }
    </Subnav>
  );
};

type ProjectsTableProps = {
  params: {
    packageLocator: string;
    version: string;
  };
};

export const ProjectsTable: VoidFunctionComponent<ProjectsTableProps> = ({ params }) => {
  const searchParams = usePackageVersionSearchParams();

  return (
    <div className="container padding-top-lg" data-test-id="package-inventory-package-version">
      <SidebarLayout>
        <SidebarLayout.Main>
          <PackageProjectsTable
            packageLocator={params.packageLocator}
            version={params.version}
            searchParams={searchParams}
          />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <PackageInventoryFilters>
            <PackageVersionFiltersPackagesContainer
              filters={searchParams.filters}
              setFilters={searchParams.setFilters}
            />
            <PackageInventoryProjectFilters
              filters={searchParams.filters}
              setFilters={searchParams.setFilters}
            />
          </PackageInventoryFilters>
        </SidebarLayout.Sidebar>
      </SidebarLayout>
    </div>
  );
};

type PackageVersionContainerProps = RouterProps<SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>>;

const PackageVersionContainer: FunctionComponent<PackageVersionContainerProps> = ({
  location,
  params,
  children,
}) => {
  const maybeNavKey = _.last(location.pathname.split('/'));
  const navKey: NavKey = isNavKey(maybeNavKey) ? maybeNavKey : 'projects';
  const searchParams = usePackageVersionSearchParams();

  return (
    <>
      <Helmet
        title={`${params.packageLocator}$${params.version}`}
        titleTemplate="%s - Package Inventory | FOSSA"
        defaultTitle="Package Inventory | FOSSA"
      />
      <PackageVersionCTA params={params} searchParams={searchParams} />
      <SecurityRequired>
        <SubNav
          navKey={navKey}
          packageLocator={params.packageLocator}
          version={params.version}
          searchParams={searchParams}
        />
      </SecurityRequired>
      {children}
    </>
  );
};

export default PackageVersionContainer;
